/**
 * Partner "on-behalf-of-member" context (area I).
 *
 * When a partner opens a consented member's diagnostic/action-plan, we set the
 * member's enterprise-profile id here. Two consumers read it so the *existing*
 * questionnaire + action-plan features work unchanged in partner mode:
 *
 *  - the API client middleware appends `?on_behalf_of=<id>` to member-scoped
 *    requests (the backend resolves the target member + checks consent);
 *  - the React Query key factories suffix their keys with it, so a member's
 *    data never collides with the partner's own cached data.
 *
 * It's a module singleton (not React state) precisely so those non-React
 * consumers can read it; an OnBehalfProvider keeps it in sync with the route.
 */

let onBehalfOf: string | null = null;

export function getOnBehalfOf(): string | null {
  return onBehalfOf;
}

export function setOnBehalfOf(profileId: string | null): void {
  onBehalfOf = profileId;
}

/**
 * Is the current on-behalf context consultation-only?
 *
 * `ReadOnlyProvider` says so to React, but only two questionnaire components
 * consume it — nothing in the action-plan feature does, so that surface
 * rendered editable controls in a mode that is meant to be read. Sixteen
 * mutation hooks funnel through one client, so the honest place to say "this
 * context does not write" is here, next to the flag that says whose data it
 * is: one rule, and a feature added later cannot forget to opt in.
 *
 * The React context stays — it is what disables a control before the user
 * clicks it. This is the floor under it, not a replacement.
 */
let onBehalfReadOnly = false;

export function isOnBehalfReadOnly(): boolean {
  return onBehalfOf != null && onBehalfReadOnly;
}

export function setOnBehalfReadOnly(readOnly: boolean): void {
  onBehalfReadOnly = readOnly;
}

/** Paths whose data is member-scoped and should carry `on_behalf_of`. */
const SCOPED_PATHS = [
  "/api/v1/questionnaire/",
  "/api/v1/action-plans/",
  "/api/v1/enterprise-profiles/",
];

export function isOnBehalfScopedPath(url: string): boolean {
  return SCOPED_PATHS.some((p) => url.includes(p));
}

/** A query-key suffix — appended only when acting on behalf, so self-mode keys
 * (and their cache) stay byte-identical to before. */
export function onBehalfKey(): readonly [] | readonly [string] {
  return onBehalfOf ? ([`onBehalfOf:${onBehalfOf}`] as const) : ([] as const);
}
